import * as THREE from 'three';

// Vertex shader for cel-shading
const celVertexShader = `
  varying vec3 vNormal;
  varying vec3 vPosition;
  varying vec2 vUv; // Pass UV coordinates to fragment shader

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
    vUv = uv; // Assign UV coordinates
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Fragment shader with Wind Waker style - vibrant colors and hard cel bands
const celFragmentShader = `
  uniform vec3 baseColor;
  uniform vec3 lightDirection;
  uniform vec3 lightColor;
  uniform float rimPower;
  uniform float rimStrength;
  uniform vec3 rimColor;

  varying vec3 vNormal;
  varying vec3 vPosition;

  // Function to boost saturation (renamed from saturate to avoid GLSL macro conflict)
  vec3 boostSaturation(vec3 color, float amount) {
    float gray = dot(color, vec3(0.299, 0.587, 0.114));
    return mix(vec3(gray), color, amount);
  }

  void main() {
    vec3 lightDir = normalize(lightDirection);
    vec3 normal = normalize(vNormal);
    float diffuse = max(0.0, dot(normal, lightDir));

    // Wind Waker style: Harder cel-shading bands with more contrast
    float celDiffuse;
    if (diffuse > 0.7) {
      celDiffuse = 1.1; // Bright highlight (slightly over 1 for bloom)
    } else if (diffuse > 0.35) {
      celDiffuse = 0.85; // Mid tone
    } else {
      celDiffuse = 0.55; // Shadow (not too dark)
    }

    // Boost the base color saturation for more vibrant look
    vec3 vibrantBase = boostSaturation(baseColor, 1.4);

    // Apply lighting with higher ambient for that bright Wind Waker look
    vec3 base = vibrantBase * (celDiffuse * lightColor + vec3(0.35));

    // Rim lighting for that glowy edge effect
    vec3 viewDir = normalize(-vPosition);
    float rim = 1.0 - max(0.0, dot(viewDir, normal));
    rim = pow(rim, rimPower);
    rim *= rimStrength;

    // Add warm rim highlight
    vec3 warmRim = mix(rimColor, vec3(1.0, 0.9, 0.7), 0.3);
    vec3 finalColor = base + rim * warmRim;

    // Slight color boost for extra pop
    finalColor = pow(finalColor, vec3(0.95));

    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

// Create a cel-shaded material (now accepts texture)
export function createCelMaterial(color = 0x3f51b5, lightDirection = new THREE.Vector3(0.5, 1, 0.5), lightColor = new THREE.Color(0xffffff), texture = null) {
  const uniforms = {
    lightDirection: { value: lightDirection.normalize() },
    lightColor: { value: new THREE.Color(lightColor) },
    baseColor: { value: new THREE.Color(color) },
    textureMap: { value: texture },
    useTexture: { value: texture !== null } // Use texture if provided
  };
  
  return new THREE.ShaderMaterial({
    uniforms: uniforms,
    vertexShader: celVertexShader,
    fragmentShader: celFragmentShader,
    side: THREE.FrontSide
  });
}

// Apply cel-shading to a mesh (passing texture if needed)
export function applyCelShading(mesh, color, texture = null) {
  const celMaterial = createCelMaterial(color, undefined, undefined, texture);
  mesh.material = celMaterial;
  return mesh;
}

// Modified createCelMesh function to accept rim parameters
export function createCelMesh(geometry, color, lightDirection = new THREE.Vector3(0.5, 1, 0.5), lightColor = new THREE.Color(0xffffff), rimColor = new THREE.Color(0x000000), rimPower = 2.0, rimStrength = 0.3) {
  const uniforms = {
    baseColor: { value: new THREE.Color(color) },
    lightDirection: { value: lightDirection.normalize() },
    lightColor: { value: lightColor },
    rimColor: { value: rimColor },
    rimPower: { value: rimPower },
    rimStrength: { value: rimStrength }
  };
  
  const material = new THREE.ShaderMaterial({
    uniforms: uniforms,
    vertexShader: celVertexShader, // Assuming celVertexShader is defined above this snippet
    fragmentShader: celFragmentShader,
  });
  
  const mesh = new THREE.Mesh(geometry, material);
  mesh.userData.uniforms = uniforms; // Store uniforms for potential updates
  return mesh;
} 